import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, History } from "lucide-react";
import { staffApi } from "./staffApi";
import { PinModal } from "./PinModal";

const HIST_META = {
  "86": ["86'D", "text-[#FF5A4E] bg-[#FF5A4E]/15"],
  back_on: ["BACK ON", "text-[#2ECC71] bg-[#2ECC71]/15"],
  pos_confirmed: ["POS CONFIRMED", "text-[#D4AF37] bg-[#D4AF37]/15"],
};
const TZ = { timeZone: "America/Chicago" }; // the restaurant's wall-time, wherever the viewer is
const when = (iso) => new Date(iso).toLocaleString([], { ...TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const hhmm = (iso) => new Date(iso).toLocaleTimeString([], { ...TZ, hour: "numeric", minute: "2-digit" });
const dur = (m) => (m == null ? "" : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

export function StaffDishes() {
  const [q, setQ] = useState("");
  const [dishes, setDishes] = useState(null);
  const [awaiting, setAwaiting] = useState(0);
  const [pinFor, setPinFor] = useState(null); // { dish, next }
  const [pinErr, setPinErr] = useState("");
  const [hist, setHist] = useState(null); // null = hidden
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    staffApi.get("/staff/dishes/digest").then((r) => setDigest(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    staffApi.get(`/staff/dishes${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`)
      .then((r) => { setDishes(r.data.dishes); setAwaiting(r.data.awaiting_pos); })
      .catch(() => {});
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000); // amber flips to confirmed when the POS event lands
    return () => clearInterval(t);
  }, [load]);

  const act = async (pin) => {
    try {
      await staffApi.post(`/staff/dishes/${pinFor.dish.slug}/availability`,
        { pin, available: pinFor.next });
      toast.success(`${pinFor.dish.name} ${pinFor.next ? "BACK ON" : "86'D"} — awaiting POS confirmation`);
      setPinFor(null); setPinErr("");
      load();
    } catch (e) {
      setPinErr(e?.response?.data?.detail || "Failed — try again");
    }
  };

  const chip = (d) => {
    if (d.pending_pos)
      return [d.available ? "BACK ON — AWAITING POS" : "86'D LOCALLY — AWAITING POS",
              "text-[#FFB020] bg-[#FFB020]/15 border border-[#FFB020]/40"];
    if (!d.available) return ["86'D", "text-[#FF5A4E] bg-[#FF5A4E]/15"];
    return ["ON MENU", "text-[#2ECC71] bg-[#2ECC71]/15"];
  };

  return (
    <div data-testid="staff-dishes" className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-black tracking-wide">DISHES</h2>
          <p className="text-white/45 text-sm mt-1">
            86 stops a dish selling on every channel. The POS owns the menu — amber means
            the POS hasn't confirmed yet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {awaiting > 0 && (
            <span data-testid="dishes-awaiting-pos" className="rounded-full px-3 py-1.5 text-[11px] font-black tracking-wider text-[#FFB020] bg-[#FFB020]/15 border border-[#FFB020]/40">
              AWAITING POS: {awaiting}
            </span>
          )}
          <button data-testid="dishes-history-btn"
            onClick={() => (hist === null
              ? staffApi.get("/staff/dishes/history").then((r) => setHist(r.data.rows)).catch(() => {})
              : setHist(null))}
            className={`h-10 px-4 rounded-full border text-xs font-black tracking-widest flex items-center gap-1.5 ${
              hist !== null ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70"}`}>
            <History size={13} /> HISTORY
          </button>
        </div>
      </div>
      {digest?.lines?.length > 0 && (
        <div data-testid="dishes-digest" className="mt-4 s-card rounded-2xl p-4">
          <p className="text-[11px] font-black tracking-[0.25em] text-white/40">YESTERDAY'S 86s</p>
          {digest.lines.map((l, i) => (
            <p key={i} data-testid={`digest-line-${i}`} className="text-sm mt-1.5">
              <b>{l.name}</b>
              <span className="text-white/50"> — off {hhmm(l.off_at)}</span>
              {l.still_off
                ? <span className="text-[#FF5A4E] font-black"> · STILL OFF</span>
                : <span className="text-white/50"> → back {hhmm(l.back_at)} ({dur(l.minutes_off)})</span>}
              {l.by && <span className="text-white/35"> · by {l.by}</span>}
            </p>
          ))}
        </div>
      )}
      {hist !== null && (
        <div data-testid="dishes-history-panel" className="mt-4 s-card rounded-2xl p-4">
          <p className="text-[11px] font-black tracking-[0.25em] text-white/40">86 TRAIL — SIGNED, NEWEST FIRST</p>
          {hist.length === 0 ? (
            <p className="text-white/40 text-sm mt-3">Nothing yet — no dish has been 86'd.</p>
          ) : (
            <div className="mt-2 divide-y divide-white/8">
              {hist.map((r, i) => {
                const [label, cls] = HIST_META[r.action] || [r.action, "text-white/60 bg-white/5"];
                return (
                  <div key={i} data-testid={`hist-row-${i}`} className="py-2 flex items-center gap-3 text-sm">
                    <span className="text-white/40 font-bold shrink-0 w-28">{when(r.ts)}</span>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black tracking-wider ${cls}`}>{label}</span>
                    <span className="font-bold truncate flex-1">{r.name || r.slug}</span>
                    <span className="text-white/45 font-bold shrink-0">
                      {r.source === "menu-intake" ? "by the POS" : `by ${r.staff}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <div className="relative mt-4">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
        <input data-testid="staff-dishes-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search dishes…"
          className="w-full h-12 rounded-xl bg-white/5 border border-white/10 pl-10 pr-4 text-sm font-bold outline-none focus:border-[#D4AF37]/50" />
      </div>
      <div className="mt-4 space-y-2">
        {dishes === null ? (
          <p className="text-white/40 text-sm py-8 text-center">Loading…</p>
        ) : dishes.length === 0 ? (
          <p data-testid="staff-dishes-empty" className="text-white/40 text-sm py-8 text-center">No dishes match.</p>
        ) : dishes.map((d) => {
          const [label, cls] = chip(d);
          return (
            <div key={d.slug} data-testid={`dish86-row-${d.slug}`}
              className="s-card rounded-2xl px-4 py-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-black text-sm truncate">{d.name}</p>
                {d.category && <p className="text-white/35 text-[11px] font-bold tracking-wider uppercase">{d.category}</p>}
              </div>
              <span data-testid={`dish86-state-${d.slug}`}
                className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-black tracking-wider ${cls}`}>
                {label}
              </span>
              <button data-testid={`dish86-btn-${d.slug}`}
                onClick={() => { setPinErr(""); setPinFor({ dish: d, next: !d.available }); }}
                className={`shrink-0 h-10 px-4 rounded-full border text-xs font-black tracking-widest ${
                  d.available ? "border-[#FF5A4E]/50 text-[#FF5A4E] hover:bg-[#FF5A4E]/10"
                              : "border-[#2ECC71]/50 text-[#2ECC71] hover:bg-[#2ECC71]/10"}`}>
                {d.available ? "86" : "BACK ON"}
              </button>
            </div>
          );
        })}
      </div>
      {pinFor && (
        <PinModal
          title={pinFor.next ? `Put ${pinFor.dish.name} back on?` : `86 ${pinFor.dish.name}?`}
          subtitle={pinFor.next ? "The POS confirms before it counts as final."
                                : "Stops it selling everywhere. Signed with your PIN."}
          error={pinErr} onSubmit={act}
          onClose={() => { setPinFor(null); setPinErr(""); }} />
      )}
    </div>
  );
}
